import React, {useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {userGetAll, userExcel} from '../../../redux/slices/user.slice'

import {pagination} from '../../../helpers'
import {
  useMemoizedColumns,
  useMemoizedHandlers,
  useMemoizedStaticData,
} from '../../../utils/performanceOptimizations'

const selectUserData = (state) => ({
  user: state.user.user,
  loading: state.user.loading,
})

function List() {
  const intl = useIntl()
  const {user, loading} = useSelector(selectUserData)
  const dispatch = useDispatch()
  const pageKey = 'USERS'

  const [params, setParams] = useState(pagination)

  const handlers = useMemoizedHandlers(
    {
      handleSorting: (item) => {
        setParams((prevState) => ({
          ...prevState,
          sort: item && item.sort,
          order: item && item.order,
        }))
      },
      handleSearch: (item) => {
        setParams((prevState) => ({...prevState, name: item}))
      },
      handlePagination: (pagination) => {
        setParams((prevState) => ({...prevState, ...pagination}))
      },
      handleExcelExport: () => {
        dispatch(
          userExcel({
            params,
            fileName: intl.formatMessage({id: 'BC.USERS'}),
          })
        )
      },
    },
    {
      handleSorting: [],
      handleSearch: [],
      handlePagination: [],
      handleExcelExport: [dispatch, params, intl],
    }
  )

  useEffect(() => {
    dispatch(userGetAll(params))
  }, [dispatch, params])

  const columns = useMemoizedColumns(
    [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'USER.SEARCH.DISPLAYNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'displayName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'USER.SEARCH.EMAIL'})}
            className='min-w-125px'
          />
        ),
        accessor: 'email',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handlers.handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handlers.handleSorting]
  )

  const buttons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.USER.ADD',
        to: '/admin/users/create',
        icon: '/images/icons/duotune/arrows/arr075.svg',
        variant: 'primary',
      },
    ],
    []
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handlers.handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
    ],
    [handlers.handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.USERS'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handlers.handleSearch}
          buttons={buttons}
          actionButtons={actionButtons}
          pages={pageKey}
        />
        <KTCardBody>
          {!user && <ListLoading />}
          {user && (
            <Table
              items={user}
              cols={columns}
              handlePagination={handlers.handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}

export {List}
